import React from 'react';
import {
  ExportProductTier,
  ExportJurisdiction,
  ExportComplianceStatus,
} from 'api/ExportHubAPI';

export const TIER_OPTIONS: ExportProductTier[] = [
  'Component',
  'Sub-assembly',
  'Assembly',
];
export const JURISDICTION_OPTIONS: ExportJurisdiction[] = [
  'ITAR',
  'EAR',
  'NRC',
  'EAR99',
  'Not Subject',
  // Last: it is the absence of a determination, not a peer classification.
  // Still selectable so a reviewer can walk a product back to undetermined,
  // and so the select can render a product that is already Unknown.
  'Unknown',
];
export const STATUS_OPTIONS: ExportComplianceStatus[] = [
  'Compliant',
  'In Review',
  'Action Required',
  'Restricted',
  'Unscreened',
  'Needs Input',
];

// CSV import template — column order matches the ExportProduct datapoints we
// persist (CreateExportProductInput). Headers are snake_case so an exact upload
// auto-maps without manual matching. Used by the Create Database wizard.
export const PRODUCT_TEMPLATE_COLUMNS: string[] = [
  'name',
  'internal_sku',
  'description',
  'manufacturer',
  'country_of_origin',
  'materials',
  'intended_end_use',
  'hardware_authorized',
  'tech_data_authorized',
  'quantity',
  'unit_of_measure',
  'hts_code',
  // ECCN drives the deterministic EAR control + Country Chart results — required
  // for EAR-controlled products (leave blank for EAR99 / ITAR / Not Subject).
  'eccn',
  'intended_destination_countries',
  // --- Structure (BOM) --------------------------------------------------
  // `tier` says what a row IS; `parent_sku` says what it BELONGS TO. Point a
  // row at another row's internal_sku (or its name) and the wizard builds the
  // parent→child link on import, so the hierarchy arrives with the products.
  // Leave both blank for a flat catalog. Tier is inferred from the links when
  // it's blank. `bom_quantity` is how many of this row go into its parent —
  // distinct from `quantity`, which is the product's own on-hand count.
  'tier',
  'parent_sku',
  'bom_quantity',
  'jurisdiction',
  'compliance_status',
];

// Columns that describe the hierarchy rather than the product itself. The
// wizard's Structure step keys off whether these were mapped.
export const PRODUCT_PARENT_COLUMN = 'parent_sku';
export const PRODUCT_BOM_QUANTITY_COLUMN = 'bom_quantity';

// The downloadable CSV template ships with headers only — no example rows — so
// customers never see placeholder/sample products in their own template. The
// column header names (PRODUCT_TEMPLATE_COLUMNS) document the expected format on
// their own; keeping this empty avoids seeding fictional data into a new import.
export const PRODUCT_TEMPLATE_EXAMPLE_ROWS: Record<string, string>[] = [];

// Jurisdiction pill color rises with control level (spec §4.6).
const JUR_VARIANT: Record<ExportJurisdiction, string> = {
  ITAR: 'danger',
  NRC: 'warning',
  EAR: 'info',
  EAR99: 'muted',
  'Not Subject': 'success',
  // Neutral, not green: an undetermined product is an open question, and a
  // success-coloured pill would read as "cleared".
  Unknown: 'muted',
};

const STATUS_VARIANT: Record<ExportComplianceStatus, string> = {
  Compliant: 'success',
  'In Review': 'info',
  'Action Required': 'warning',
  Restricted: 'danger',
  Unscreened: 'muted',
  'Needs Input': 'warning',
};

export const JurisdictionPill: React.FC<{ value: ExportJurisdiction }> = ({
  value,
}) => (
  <span className={`eh-pill eh-pill-${JUR_VARIANT[value] || 'muted'}`}>
    {value}
  </span>
);

export const StatusPill: React.FC<{ value: ExportComplianceStatus }> = ({
  value,
}) => (
  <span className={`eh-pill eh-pill-${STATUS_VARIANT[value] || 'muted'}`}>
    {value}
  </span>
);

export const TierBadge: React.FC<{ value: ExportProductTier }> = ({ value }) => (
  <span className='eh-tier-badge' title={`Tier: ${value}`}>
    {value}
  </span>
);
